import { useTheme, FormComponentProps, ProgrammeTypeDto } from '@ecdlink/core';
import {
  BannerWrapper,
  Typography,
  Button,
  ButtonGroup,
  ButtonGroupTypes,
  renderIcon,
} from '@ecdlink/ui';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { classroomsActions, classroomsSelectors } from '@/store/classroom';
import { staticDataSelectors } from '@store/static-data';
import { useForm, useFormState, Controller } from 'react-hook-form';
import { EditProgrammeModel, editProgrammeSchema } from './edit-programme';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '@/store';
import ROUTES from '@routes/routes';

export interface EditProgrammeFormProps
  extends FormComponentProps<EditProgrammeModel> {
  programme?: EditProgrammeModel;
}

export const ProgrammeType: React.FC<EditProgrammeFormProps> = ({
  programme,
}) => {
  const {
    getValues: getProgrammeFormValues,
    setValue: setProgrammeFormValue,
    reset: resetProgrammeFormValue,
    control: programmeFormControl,
  } = useForm<EditProgrammeModel>({
    resolver: yupResolver(editProgrammeSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });
  const history = useHistory();
  const { theme } = useTheme();
  const { isOnline } = useOnlineStatus();
  const { isValid } = useFormState({ control: programmeFormControl });
  const appDispatch = useAppDispatch();
  const classroomGroups = useSelector(
    classroomsSelectors?.getAllClassroomGroups
  );

  const programData = useSelector(staticDataSelectors.getProgrammeTypes);

  useEffect(() => {
    if (classroomGroups?.[0]?.programmeType?.id) {
      setProgrammeFormValue('type', classroomGroups?.[0]?.programmeType?.id);
    }
  }, [setProgrammeFormValue]);

  const onSubmit = (e: EditProgrammeModel) => {
    const programmeInput = programData?.find((x) => x.id === e.type);
    if (programmeInput) {
      appDispatch(classroomsActions.setProgrammeType(programmeInput));
    }
    history.push(ROUTES.PRACTITIONER.PROGRAMME_INFORMATION);
  };

  return (
    <>
      <BannerWrapper
        backgroundUrl={theme?.images.graphicOverlayUrl}
        backgroundImageColour={'primary'}
        title={`Type of ECD Service`}
        color={'primary'}
        size="normal"
        renderBorder={true}
        renderOverflow={false}
        onBack={history.goBack}
        displayOffline={!isOnline}
      />
      <div className="h-screen overflow-y-scroll p-4">
        <Typography
          type={'h2'}
          text={'What type of programme are you running?'}
          color={'textDark'}
        />
        <div className="mt-1">
          <Controller
            name={'type'}
            control={programmeFormControl}
            render={({ field: { onChange, value, ref } }) => (
              <ButtonGroup<string>
                inputRef={ref}
                options={
                  (programData &&
                    programData.map((x: ProgrammeTypeDto) => {
                      return { text: x.description, value: x.id ?? '' };
                    })) ||
                  []
                }
                onOptionSelected={onChange}
                selectedOptions={value}
                color="secondary"
                type={ButtonGroupTypes.Button}
                className={'w-full'}
              />
            )}
          ></Controller>
        </div>
        <div className="mt-2">
          <Button
            type="filled"
            color="primary"
            className={'mt-1 mb-2 w-full'}
            disabled={!isValid}
            onClick={() => onSubmit(getProgrammeFormValues())}
          >
            {renderIcon('ArrowCircleRightIcon', 'mr-2 text-white w-5')}
            <Typography type={'help'} text={'Save'} color={'white'} />
          </Button>
        </div>
      </div>
    </>
  );
};
